import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import authService from './services/authService';
import './html5up-theme.css';
import './SelectionPage.css';

const SelectionPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [outputs, setOutputs] = useState([]);
    const [filteredOutputs, setFilteredOutputs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filter states - initialize from URL params
    const [virusFilter, setVirusFilter] = useState(searchParams.get('virus') || '');
    const [proteinFilter, setProteinFilter] = useState(searchParams.get('protein') || '');
    const [mutationFilter, setMutationFilter] = useState(searchParams.get('mutation') || '');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
    const [uniqueViruses, setUniqueViruses] = useState([]);
    const [uniqueProteins, setUniqueProteins] = useState([]);
    const [uniqueMutations, setUniqueMutations] = useState([]);
    
    // Search states for dropdowns - initialize from URL params
    const [virusSearch, setVirusSearch] = useState(searchParams.get('virus') || '');
    const [proteinSearch, setProteinSearch] = useState(searchParams.get('protein') || '');
    const [mutationSearch, setMutationSearch] = useState(searchParams.get('mutation') || '');
    const [virusDropdownOpen, setVirusDropdownOpen] = useState(false);
    const [proteinDropdownOpen, setProteinDropdownOpen] = useState(false);
    const [mutationDropdownOpen, setMutationDropdownOpen] = useState(false);
    
    // Sorting states - initialize from URL params
    const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'default');
    const [sortOrder, setSortOrder] = useState(searchParams.get('order') || 'asc');

    // Profile modal states
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [profileChoices, setProfileChoices] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        fetchOutputs();
        checkAuthStatus();
        
        // Add event listener to refresh data when page becomes visible
        // This handles cases when user returns from evaluation page
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                console.log('Page became visible, refreshing data...');
                fetchOutputs();
            }
        };
        
        const handleFocus = () => {
            console.log('Page gained focus, refreshing data...');
            fetchOutputs();
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    useEffect(() => {
        applyFiltersAndSort();
    }, [outputs, virusFilter, proteinFilter, mutationFilter, statusFilter, sortBy, sortOrder]);

    // Update URL params when filter/sort state changes
    useEffect(() => {
        const newParams = new URLSearchParams();
        if (virusFilter) newParams.set('virus', virusFilter);
        if (proteinFilter) newParams.set('protein', proteinFilter);
        if (mutationFilter) newParams.set('mutation', mutationFilter);
        if (statusFilter) newParams.set('status', statusFilter);
        if (sortBy !== 'default') newParams.set('sort', sortBy);
        if (sortOrder !== 'asc') newParams.set('order', sortOrder);
        
        setSearchParams(newParams);
    }, [virusFilter, proteinFilter, mutationFilter, statusFilter, sortBy, sortOrder, setSearchParams]);

    // Reset sort to default when switching to Pending status (no timestamps available)
    useEffect(() => {
        if (statusFilter === 'Pending' && (sortBy === 'recent' || sortBy === 'oldest')) {
            setSortBy('default');
        }
    }, [statusFilter, sortBy]);

    const fetchOutputs = async () => {
        try {
            setLoading(true);
            
            // Try to fetch from Django backend with authentication
            try {
                const headers = {
                    'Content-Type': 'application/json',
                };
                
                // Add authentication token if available
                const token = authService.getToken();
                if (token) {
                    headers['Authorization'] = `Token ${token}`;
                }
                
                const response = await fetch('/api/mutation-reasoning-outputs/', {
                    method: 'GET',
                    headers: headers,
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json();
                    setOutputs(data);
                    
                    // Extract unique values for filters
                    const viruses = [...new Set(data.map(output => output.virus_name))].sort();
                    const proteins = [...new Set(data.map(output => output.protein_name))].sort();
                    
                    // Extract unique mutations from all outputs
                    const mutations = data
                        .map(output => output.mutation)
                        .filter(mutation => mutation) // Remove empty/null mutations
                    const uniqueMutationsArray = [...new Set(mutations)].sort();
                    
                    setUniqueViruses(viruses);
                    setUniqueProteins(proteins);
                    setUniqueMutations(uniqueMutationsArray);
                } else {
                    // Backend not available or no data - show empty state
                    console.log('Backend not available, showing empty state');
                    setOutputs([]);
                    setUniqueViruses([]);
                    setUniqueProteins([]);
                }
            } catch (fetchError) {
                // Backend not running - show empty state with message
                console.log('Django backend not running, showing empty state');
                setOutputs([]);
                setUniqueViruses([]);
                setUniqueProteins([]);
                setError('Backend service unavailable. Please check if the backend is running.');
            }
            
            setLoading(false);
        } catch (err) {
            setError(`Error loading data: ${err.message}`);
            setLoading(false);
        }
    };

    const applyFiltersAndSort = () => {
        let filtered = outputs;
        
        // Apply filters
        if (virusFilter) {
            filtered = filtered.filter(output => output.virus_name === virusFilter);
        }
        
        if (proteinFilter) {
            filtered = filtered.filter(output => output.protein_name === proteinFilter);
        }
        
        if (mutationFilter) {
            filtered = filtered.filter(output => {
                return output.mutation === mutationFilter;
            });
        }
        
        if (statusFilter) {
            filtered = filtered.filter(output => {
                const status = output.evaluation_status || 'Pending';
                return status === statusFilter;
            });
        }
        
        // Apply sorting
        filtered = [...filtered].sort((a, b) => {
            let comparison = 0;
            
            switch (sortBy) {
                case 'default':
                    // Randomized default order to avoid bias in evaluation
                    // Use a seeded random function based on output IDs for consistency within session
                    const seedA = a.id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                    const seedB = b.id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                    comparison = Math.sin(seedA * 12.9898 + seedB * 78.233) * 43758.5453;
                    comparison = comparison - Math.floor(comparison); // Get fractional part
                    return comparison - 0.5; // Center around 0
                case 'recent':
                    // Most recent first (desc order by timestamp)
                    // Items without timestamps (pending) go to bottom
                    const hasTimeA = !!a.evaluation_timestamp;
                    const hasTimeB = !!b.evaluation_timestamp;
                    
                    if (hasTimeA && hasTimeB) {
                        // Both have timestamps - sort by time (newest first)
                        const timeA = new Date(a.evaluation_timestamp).getTime();
                        const timeB = new Date(b.evaluation_timestamp).getTime();
                        comparison = timeB - timeA;
                    } else if (hasTimeA && !hasTimeB) {
                        // A has timestamp, B doesn't - A comes first
                        comparison = -1;
                    } else if (!hasTimeA && hasTimeB) {
                        // B has timestamp, A doesn't - B comes first
                        comparison = 1;
                    } else {
                        // Neither has timestamp - maintain original order
                        comparison = 0;
                    }
                    break;
                case 'oldest':
                    // Oldest first (asc order by timestamp)
                    // Items without timestamps (pending) go to bottom
                    const hasTimeA2 = !!a.evaluation_timestamp;
                    const hasTimeB2 = !!b.evaluation_timestamp;
                    
                    if (hasTimeA2 && hasTimeB2) {
                        // Both have timestamps - sort by time (oldest first)
                        const timeA2 = new Date(a.evaluation_timestamp).getTime();
                        const timeB2 = new Date(b.evaluation_timestamp).getTime();
                        comparison = timeA2 - timeB2;
                    } else if (hasTimeA2 && !hasTimeB2) {
                        // A has timestamp, B doesn't - A comes first
                        comparison = -1;
                    } else if (!hasTimeA2 && hasTimeB2) {
                        // B has timestamp, A doesn't - B comes first
                        comparison = 1;
                    } else {
                        // Neither has timestamp - maintain original order
                        comparison = 0;
                    }
                    break;
                case 'virus-asc':
                    // Sort by virus name A-Z
                    comparison = a.virus_name.localeCompare(b.virus_name);
                    break;
                case 'virus-desc':
                    // Sort by virus name Z-A
                    comparison = b.virus_name.localeCompare(a.virus_name);
                    break;
                case 'protein-asc':
                    // Sort by protein name A-Z
                    comparison = a.protein_name.localeCompare(b.protein_name);
                    break;
                case 'protein-desc':
                    // Sort by protein name Z-A
                    comparison = b.protein_name.localeCompare(a.protein_name);
                    break;
                case 'mutation-asc':
                    // Sort by mutation A-Z (items without mutations go to bottom)
                    const mutationA = a.mutation || '';
                    const mutationB = b.mutation || '';
                    
                    if (mutationA && mutationB) {
                        comparison = mutationA.localeCompare(mutationB);
                    } else if (mutationA && !mutationB) {
                        comparison = -1; // A has mutation, B doesn't - A comes first
                    } else if (!mutationA && mutationB) {
                        comparison = 1; // B has mutation, A doesn't - B comes first
                    } else {
                        comparison = 0; // Neither has mutations
                    }
                    break;
                case 'mutation-desc':
                    // Sort by mutation Z-A (items without mutations go to bottom)
                    const mutationA2 = a.mutation || '';
                    const mutationB2 = b.mutation || '';
                    
                    if (mutationA2 && mutationB2) {
                        comparison = mutationB2.localeCompare(mutationA2);
                    } else if (mutationA2 && !mutationB2) {
                        comparison = -1; // A has mutation, B doesn't - A comes first
                    } else if (!mutationA2 && mutationB2) {
                        comparison = 1; // B has mutation, A doesn't - B comes first
                    } else {
                        comparison = 0; // Neither has mutations
                    }
                    break;
                default:
                    comparison = 0;
            }
            
            return comparison;
        });
        
        setFilteredOutputs(filtered);
    };

    const handleSelectOutput = (outputId) => {
        // Store current filter/sort state in URL for when user returns
        const currentParams = searchParams.toString();
        const returnUrl = currentParams ? `/select?${currentParams}` : '/select';
        
        // Navigate to evaluation page with return URL context
        navigate(`/evaluate/${outputId}`, { 
            state: { returnUrl } 
        });
    };

    const clearAllFilters = () => {
        setVirusFilter('');
        setProteinFilter('');
        setMutationFilter('');
        setStatusFilter('');
        setVirusSearch('');
        setProteinSearch('');
        setMutationSearch('');
        setSortBy('default');
        setSortOrder('asc');
        // Clear URL params
        setSearchParams(new URLSearchParams());
    };

    // Helper functions for searchable dropdowns
    const getFilteredViruses = () => {
        if (!virusSearch) return uniqueViruses;
        return uniqueViruses.filter(virus => 
            virus.toLowerCase().includes(virusSearch.toLowerCase())
        );
    };

    const getFilteredProteins = () => {
        if (!proteinSearch) return uniqueProteins;
        return uniqueProteins.filter(protein => 
            protein.toLowerCase().includes(proteinSearch.toLowerCase())
        );
    };

    const getFilteredMutations = () => {
        if (!mutationSearch) return uniqueMutations;
        return uniqueMutations.filter(mutation => 
            mutation.toLowerCase().includes(mutationSearch.toLowerCase())
        );
    };

    const handleVirusSearch = (e) => {
        setVirusSearch(e.target.value);
        setVirusDropdownOpen(true);
    };

    const handleProteinSearch = (e) => {
        setProteinSearch(e.target.value);
        setProteinDropdownOpen(true);
    };

    const handleMutationSearch = (e) => {
        setMutationSearch(e.target.value);
        setMutationDropdownOpen(true);
    };

    const selectVirus = (virus) => {
        setVirusFilter(virus);
        setVirusSearch(virus);
        setVirusDropdownOpen(false);
    };

    const selectProtein = (protein) => {
        setProteinFilter(protein);
        setProteinSearch(protein);
        setProteinDropdownOpen(false);
    };

    const selectMutation = (mutation) => {
        setMutationFilter(mutation);
        setMutationSearch(mutation);
        setMutationDropdownOpen(false);
    };

    const handleVirusInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            const filteredViruses = getFilteredViruses();
            if (filteredViruses.length === 1) {
                selectVirus(filteredViruses[0]);
            } else if (filteredViruses.includes(virusSearch)) {
                selectVirus(virusSearch);
            }
        } else if (e.key === 'Escape') {
            setVirusDropdownOpen(false);
            setVirusSearch(virusFilter);
        }
    };

    const handleProteinInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            const filteredProteins = getFilteredProteins();
            if (filteredProteins.length === 1) {
                selectProtein(filteredProteins[0]);
            } else if (filteredProteins.includes(proteinSearch)) {
                selectProtein(proteinSearch);
            }
        } else if (e.key === 'Escape') {
            setProteinDropdownOpen(false);
            setProteinSearch(proteinFilter);
        }
    };

    const handleMutationInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            const filteredMutations = getFilteredMutations();
            if (filteredMutations.length === 1) {
                selectMutation(filteredMutations[0]);
            } else if (filteredMutations.includes(mutationSearch)) {
                selectMutation(mutationSearch);
            }
        } else if (e.key === 'Escape') {
            setMutationDropdownOpen(false);
            setMutationSearch(mutationFilter);
        }
    };

    // Helper function to extract anonymous ID (content hash only) for display
    const getAnonymousDisplayId = (display_id) => {
        if (!display_id) return '';
        
        // For new format: "modelcode.contenthash" -> return only "contenthash"
        if (display_id.includes('.')) {
            const parts = display_id.split('.');
            // Return the last part (content hash) which is the most unique identifier
            return parts[parts.length - 1];
        }
        
        // For old format or single part, return as-is
        return display_id;
    };

    // Helper function to display single mutation
    const getMutationDisplay = (mutation) => {
        if (!mutation) {
            return '—';
        }
        
        return mutation;
    };

    // Profile management functions
    const checkAuthStatus = async () => {
        try {
            const token = authService.getToken();
            setIsAuthenticated(!!token);
        } catch (error) {
            console.log('Auth check failed:', error);
            setIsAuthenticated(false);
        }
    };

    const fetchProfile = async () => {
        try {
            setProfileLoading(true);
            setProfileError('');

            const token = authService.getToken();
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await fetch('/api/profile/me/', {
                method: 'GET',
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setProfileData(data);
            } else {
                throw new Error('Failed to fetch profile');
            }
        } catch (error) {
            console.error('Profile fetch error:', error);
            setProfileError(`Failed to load profile: ${error.message}`);
        } finally {
            setProfileLoading(false);
        }
    };

    const fetchProfileChoices = async () => {
        try {
            const token = authService.getToken();
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await fetch('/api/profile/choices/', {
                method: 'GET',
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setProfileChoices(data);
            } else {
                throw new Error('Failed to fetch profile choices');
            }
        } catch (error) {
            console.error('Profile choices fetch error:', error);
        }
    };

    const handleProfileClick = async () => {
        if (!isAuthenticated) {
            alert('Please log in to access your profile.');
            return;
        }

        setShowProfileModal(true);
        await Promise.all([fetchProfile(), fetchProfileChoices()]);
    };

    const handleProfileSave = async (updatedData) => {
        try {
            setProfileLoading(true);
            setProfileError('');

            const token = authService.getToken();
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await fetch('/api/profile/update_demographics/', {
                method: 'PATCH',
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(updatedData),
            });

            if (response.ok) {
                const data = await response.json();
                setProfileData(data.profile);
                alert('Profile updated successfully!');
                setShowProfileModal(false);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Profile save error:', error);
            setProfileError(`Failed to save profile: ${error.message}`);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleProfileClose = () => {
        setShowProfileModal(false);
        setProfileError('');
    };

    if (loading) {
        return (
            <div className="selection-page">
                <div className="container">
                    <div className="loading">Loading evaluation data...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="selection-page">
                <div className="container">
                    <div className="error">Error: {error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="selection-page">
            {/* VILLA Research Platform Header */}
            <header id="header">
                <h1>
                    <Link to="/" className="logo-link">
                        <img src={`${process.env.PUBLIC_URL}/compass_logo.png?v=3`} alt="VILLA Logo" className="navbar-logo" />
                        VILLA Research Platform
                    </Link>
                </h1>
                <nav>
                    <Link 
                        to="/resources"
                        className="resources-btn"
                        title="View Resources and Help"
                    >
                        📑 Resources
                    </Link>
                    {isAuthenticated && (
                        <button 
                            className="profile-btn"
                            onClick={handleProfileClick}
                            title="Manage Profile"
                        >
                            👤 Profile
                        </button>
                    )}
                </nav>
            </header>
            
            <div className="container">
                {/* Page Header */}
                <div className="page-header">
                    <h1>Select Evaluation Task</h1>
                </div>

                {/* Filters */}
                <div className="filters-section">
                    <div className="filters">
                        <div className="combined-filter-group">
                            <label>Filter by Virus, Protein, and Mutation:</label>
                            <div className="combined-dropdown-container">
                                <div className="searchable-dropdown virus-dropdown" onBlur={() => setTimeout(() => setVirusDropdownOpen(false), 200)}>
                                    <input
                                        id="virus-search"
                                        type="text"
                                        placeholder="Type to search viruses..."
                                        value={virusSearch}
                                        onChange={handleVirusSearch}
                                        onKeyDown={handleVirusInputKeyDown}
                                        onFocus={() => setVirusDropdownOpen(true)}
                                    />
                                    <button 
                                        type="button" 
                                        className="dropdown-toggle"
                                        onClick={() => setVirusDropdownOpen(!virusDropdownOpen)}
                                    >
                                        ▼
                                    </button>
                                    {virusDropdownOpen && (
                                        <div className="dropdown-options">
                                            <div 
                                                className="dropdown-option"
                                                onClick={() => selectVirus('')}
                                            >
                                                All Viruses
                                            </div>
                                            {getFilteredViruses().map(virus => (
                                                <div 
                                                    key={virus}
                                                    className={`dropdown-option ${virusFilter === virus ? 'selected' : ''}`}
                                                    onClick={() => selectVirus(virus)}
                                                >
                                                    {virus}
                                                </div>
                                            ))}
                                            {getFilteredViruses().length === 0 && (
                                                <div className="dropdown-option no-results">
                                                    No matches found
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="dropdown-divider"></div>
                                
                                <div className="searchable-dropdown protein-dropdown" onBlur={() => setTimeout(() => setProteinDropdownOpen(false), 200)}>
                                    <input
                                        id="protein-search"
                                        type="text"
                                        placeholder="Type to search proteins..."
                                        value={proteinSearch}
                                        onChange={handleProteinSearch}
                                        onKeyDown={handleProteinInputKeyDown}
                                        onFocus={() => setProteinDropdownOpen(true)}
                                    />
                                    <button 
                                        type="button" 
                                        className="dropdown-toggle"
                                        onClick={() => setProteinDropdownOpen(!proteinDropdownOpen)}
                                    >
                                        ▼
                                    </button>
                                    {proteinDropdownOpen && (
                                        <div className="dropdown-options">
                                            <div 
                                                className="dropdown-option"
                                                onClick={() => selectProtein('')}
                                            >
                                                All Proteins
                                            </div>
                                            {getFilteredProteins().map(protein => (
                                                <div 
                                                    key={protein}
                                                    className={`dropdown-option ${proteinFilter === protein ? 'selected' : ''}`}
                                                    onClick={() => selectProtein(protein)}
                                                >
                                                    {protein}
                                                </div>
                                            ))}
                                            {getFilteredProteins().length === 0 && (
                                                <div className="dropdown-option no-results">
                                                    No matches found
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="dropdown-divider"></div>
                                
                                <div className="searchable-dropdown mutation-dropdown" onBlur={() => setTimeout(() => setMutationDropdownOpen(false), 200)}>
                                    <input
                                        id="mutation-search"
                                        type="text"
                                        placeholder="Type to search mutations..."
                                        value={mutationSearch}
                                        onChange={handleMutationSearch}
                                        onKeyDown={handleMutationInputKeyDown}
                                        onFocus={() => setMutationDropdownOpen(true)}
                                    />
                                    <button 
                                        type="button" 
                                        className="dropdown-toggle"
                                        onClick={() => setMutationDropdownOpen(!mutationDropdownOpen)}
                                    >
                                        ▼
                                    </button>
                                    {mutationDropdownOpen && (
                                        <div className="dropdown-options">
                                            <div 
                                                className="dropdown-option"
                                                onClick={() => selectMutation('')}
                                            >
                                                All Mutations
                                            </div>
                                            {getFilteredMutations().map(mutation => (
                                                <div 
                                                    key={mutation}
                                                    className={`dropdown-option ${mutationFilter === mutation ? 'selected' : ''}`}
                                                    onClick={() => selectMutation(mutation)}
                                                >
                                                    {mutation}
                                                </div>
                                            ))}
                                            {getFilteredMutations().length === 0 && (
                                                <div className="dropdown-option no-results">
                                                    No matches found
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="status-filter-group">
                            <label htmlFor="status-filter">Filter by Status:</label>
                            <select 
                                id="status-filter"
                                value={statusFilter} 
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="status-select"
                            >
                                <option value="">All Statuses</option>
                                <option value="Pending">Pending</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>

                        <div className="sort-controls">
                            <label htmlFor="sort-select">Sort by:</label>
                            <select 
                                id="sort-select"
                                value={sortBy} 
                                onChange={(e) => setSortBy(e.target.value)}
                                className="sort-select"
                            >
                                <option value="default">Default Order</option>
                                {statusFilter !== 'Pending' && (
                                    <>
                                        <option value="recent">Most Recently Submitted</option>
                                        <option value="oldest">Oldest Submitted</option>
                                    </>
                                )}
                                <option value="virus-asc">Virus (A-Z)</option>
                                <option value="virus-desc">Virus (Z-A)</option>
                                <option value="protein-asc">Protein (A-Z)</option>
                                <option value="protein-desc">Protein (Z-A)</option>
                                <option value="mutation-asc">Mutation (A-Z)</option>
                                <option value="mutation-desc">Mutation (Z-A)</option>
                            </select>
                        </div>

                        {(virusFilter || proteinFilter || mutationFilter || statusFilter || sortBy !== 'default') && (
                            <button className="clear-filters-btn" onClick={clearAllFilters}>
                                Clear All
                            </button>
                        )}
                    </div>
                    
                    <div className="results-count">
                        Showing {filteredOutputs.length} of {outputs.length} outputs
                        {(virusFilter || proteinFilter || mutationFilter || statusFilter) && (
                            <span className="filter-summary">
                                {' '}(filtered by: {[
                                    virusFilter && `virus: ${virusFilter}`,
                                    proteinFilter && `protein: ${proteinFilter}`,
                                    mutationFilter && `mutation: ${mutationFilter}`,
                                    statusFilter && `status: ${statusFilter}`
                                ].filter(Boolean).join(', ')})
                            </span>
                        )}
                    </div>
                </div>

                {/* Outputs Table */}
                <div className="outputs-section">
                    {outputs.length === 0 && !error ? (
                        <div className="no-results">
                            <p>No evaluation data available. Make sure Django backend is running and test data is loaded.</p>
                        </div>
                    ) : filteredOutputs.length === 0 ? (
                        <div className="no-results">
                            No outputs match your current filters.
                        </div>
                    ) : (
                        <div className="outputs-table-container">
                            <table className="outputs-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '10%' }}>ID</th>
                                        <th style={{ width: '20%' }}>Virus</th>
                                        <th style={{ width: '15%' }}>Protein</th>
                                        <th style={{ width: '20%' }}>Mutation</th>
                                        <th style={{ width: '12%', textAlign: 'center' }}>Status</th>
                                        <th style={{ width: '23%' }}>Submitted</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOutputs.map(output => (
                                        <tr 
                                            key={output.id} 
                                            className="clickable-row"
                                            onClick={() => handleSelectOutput(output.id)}
                                        >
                                            <td className="output-id">{getAnonymousDisplayId(output.display_id)}</td>
                                            <td>{output.virus_name}</td>
                                            <td className="protein-name">{output.protein_name}</td>
                                            <td className="mutation-display">{getMutationDisplay(output.mutation)}</td>
                                            <td className={`status ${output.evaluation_status?.toLowerCase()}`}>
                                                <span className="status-text">
                                                    {output.evaluation_status === 'Completed' ? (
                                                        <>
                                                            <span className="status-default">Completed</span>
                                                            <span className="status-hover">View/Edit</span>
                                                        </>
                                                    ) : (
                                                        output.evaluation_status || 'Pending'
                                                    )}
                                                </span>
                                            </td>
                                            <td className="timestamp">
                                                {output.evaluation_timestamp ? 
                                                    new Date(output.evaluation_timestamp).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : 
                                                    '—'
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Profile Modal */}
            {showProfileModal && (
                <ProfileModal
                    profileData={profileData}
                    profileChoices={profileChoices}
                    loading={profileLoading}
                    error={profileError}
                    onSave={handleProfileSave}
                    onClose={handleProfileClose}
                />
            )}
        </div>
    );
};

// Profile Modal Component
const ProfileModal = ({ profileData, profileChoices, loading, error, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        academic_position: '',
        virology_experience: '',
        influenza_experience: ''
    });

    useEffect(() => {
        if (profileData) {
            setFormData({
                academic_position: profileData.academic_position || '',
                virology_experience: profileData.virology_experience || '',
                influenza_experience: profileData.influenza_experience || ''
            });
        }
    }, [profileData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleModalClick = (e) => {
        // Close modal if clicking on backdrop
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="profile-modal-overlay" onClick={handleModalClick}>
            <div className="profile-modal">
                <div className="profile-modal-header">
                    <h2>User Profile</h2>
                    <button className="profile-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="profile-modal-content">
                    {loading ? (
                        <div className="profile-loading">Loading profile...</div>
                    ) : error ? (
                        <div className="profile-error">{error}</div>
                    ) : profileData ? (
                        <form onSubmit={handleSubmit}>
                            {/* Read-only Information Section */}
                            <div className="profile-section">
                                <h3>Account Information</h3>
                                <div className="profile-field-group">
                                    <div className="profile-field">
                                        <label>Email:</label>
                                        <span className="profile-value">{profileData.email}</span>
                                    </div>
                                    <div className="profile-field">
                                        <label>Display Name:</label>
                                        <span className="profile-value">
                                            {profileData.display_name || 
                                             `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || 
                                             profileData.username || 
                                             'Not provided'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Editable Demographics Section */}
                            <div className="profile-section">
                                <h3>Research Background</h3>
                                <p className="profile-section-description">
                                    This demographic information is optional and helps us understand our evaluator community for research purposes.
                                </p>
                                
                                <div className="profile-field-group">
                                    <div className="profile-field">
                                        <label htmlFor="academic-position">Academic Position:</label>
                                        <select
                                            id="academic-position"
                                            value={formData.academic_position}
                                            onChange={(e) => handleInputChange('academic_position', e.target.value)}
                                            className="profile-select"
                                        >
                                            <option value="">Select position...</option>
                                            {profileChoices?.academic_position_choices?.map(choice => (
                                                <option key={choice.value} value={choice.value}>
                                                    {choice.display}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="profile-field">
                                        <label htmlFor="virology-experience">Virology Experience:</label>
                                        <select
                                            id="virology-experience"
                                            value={formData.virology_experience}
                                            onChange={(e) => handleInputChange('virology_experience', e.target.value)}
                                            className="profile-select"
                                        >
                                            <option value="">Select experience level...</option>
                                            {profileChoices?.virology_experience_choices?.map(choice => (
                                                <option key={choice.value} value={choice.value}>
                                                    {choice.display}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="profile-field">
                                        <label htmlFor="influenza-experience">Influenza-Specific Experience:</label>
                                        <select
                                            id="influenza-experience"
                                            value={formData.influenza_experience}
                                            onChange={(e) => handleInputChange('influenza_experience', e.target.value)}
                                            className="profile-select"
                                        >
                                            <option value="">Select...</option>
                                            {profileChoices?.influenza_experience_choices?.map(choice => (
                                                <option key={choice.value} value={choice.value}>
                                                    {choice.display}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="profile-modal-actions">
                                <button type="button" className="profile-btn-secondary" onClick={onClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="profile-btn-primary" disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="profile-error">Failed to load profile data</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SelectionPage;
